import { useEffect, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MaskedLine, Overline, FadeUp } from "@/components/site/Reveal";
import ComingToFore from "@/components/site/ComingToFore";
import { postContact } from "@/lib/api";

const inputCls =
  "w-full border border-white/20 bg-black/40 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent";

const GuestForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", title: "", story: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.business,
        title: form.title,
        inquiry_type: "guest",
        message: form.story,
      });
      toast.success("Application sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", business: "", title: "", story: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="apply" className="mt-14 scroll-mt-24 border border-white/10 bg-ink-2" data-testid="guest-form-section">
      <div className="grid gap-12 p-8 md:p-14 lg:grid-cols-2 lg:gap-16">
        <FadeUp>
          <Overline>Your Turn</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-5xl" data-testid="guest-form-heading">
            Tell us<br />your <span className="text-brand-accent">story.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted">
            Founders, athletes, firefighters, comedians, veterans, musicians — and anyone with a
            story worth hearing. You don't need to be a golfer: 18 holes, no studio, no script.
            A few details and your application lands straight in our inbox.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <form onSubmit={submit} className="border border-white/10 bg-ink p-7 md:p-10" data-testid="guest-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="guest-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Name *</label>
                <input id="guest-name" data-testid="guest-form-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="guest-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Email *</label>
                <input id="guest-email" data-testid="guest-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="guest-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Phone</label>
                <input id="guest-phone" data-testid="guest-form-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className={inputCls} />
              </div>
              <div>
                <label htmlFor="guest-business" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Business / Craft</label>
                <input id="guest-business" data-testid="guest-form-business" value={form.business} onChange={set("business")} placeholder="Company, team, or craft" className={inputCls} />
              </div>
              <div>
                <label htmlFor="guest-title" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Title</label>
                <input id="guest-title" data-testid="guest-form-title" value={form.title} onChange={set("title")} placeholder="e.g. Founder, Firefighter" className={inputCls} />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="guest-story" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Your Story *</label>
              <textarea
                id="guest-story"
                data-testid="guest-form-story"
                required
                rows={5}
                value={form.story}
                onChange={set("story")}
                placeholder="Who you are, what you've built or been through, and why you'd be a great guest…"
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="guest-form-submit"
              className="group mt-8 flex w-full items-center justify-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              {sending ? "Sending…" : "Apply To Be a Guest"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Guests() {
  useEffect(() => {
    if (window.location.hash === "#apply") {
      const t = setTimeout(() => {
        document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pb-32 md:pt-48" data-testid="guests-page">
      <MaskedLine>
        <Overline>The Guest Book</Overline>
      </MaskedLine>
      <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="guests-heading">
        <MaskedLine delay={0.15}>People worth</MaskedLine>
        <MaskedLine delay={0.3}>a <span className="text-brand-accent">round.</span></MaskedLine>
      </h1>
      <FadeUp delay={0.5}>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
          Athletes. Comedians. Entrepreneurs. Creators. First responders. And people with stories
          worth hearing. No golf experience required — if you've got a story, we've got a tee time.
        </p>
      </FadeUp>

      <ComingToFore />

      <GuestForm />
    </main>
  );
}
